/**
 * Validates a file against allowed MIME types and file extensions
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @param {string[]} options.allowedTypes - Allowed MIME types (e.g., ["image/jpeg", "image/png"])
 * @param {string[]} options.allowedExtensions - Allowed file extensions (e.g., [".jpg", ".png", ".heic"])
 * @param {string} options.errorMessage - Custom error message to return if validation fails
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function validateFile(file, options = {}) {
  const {
    allowedTypes = [],
    allowedExtensions = [],
    errorMessage = "Unsupported file format. Please upload a valid file.",
  } = options;

  // If no file provided, it's valid (optional file)
  if (!file) {
    return { isValid: true, error: null };
  }

  const fileType = (file.type || "").toLowerCase();
  const fileName = (file.name || "").toLowerCase();
  const lastDotIndex = fileName.lastIndexOf(".");
  const fileExtension =
    lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";

  // Check MIME type if provided
  const isValidType =
    allowedTypes.length === 0 || (fileType && allowedTypes.includes(fileType));

  // Check file extension if provided
  const isValidExtension =
    allowedExtensions.length === 0 ||
    (fileExtension && allowedExtensions.includes(fileExtension.toLowerCase()));

  // File is valid if either type or extension matches (or both are not checked)
  if (isValidType || isValidExtension) {
    return { isValid: true, error: null };
  }

  return { isValid: false, error: errorMessage };
}

/**
 * Validates an image file (PNG, WEBP)
 * @param {File} file - The file to validate
 * @param {string} fieldName - Name of the field for error message (optional)
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function validateImageFile(file, fieldName = "") {
  const allowedTypes = ["image/png", "image/webp"];
  const allowedExtensions = [".png", ".webp"];

  const errorMessage = fieldName
    ? `Unsupported file format for ${fieldName}. Please upload a PNG or WEBP image.`
    : "Unsupported file format. Please upload a PNG or WEBP image.";

  return validateFile(file, {
    allowedTypes,
    allowedExtensions,
    errorMessage,
  });
}

/**
 * Validates a document file (PNG, WEBP, PDF)
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function validateDocumentFile(file) {
  const allowedTypes = [
    "image/png",
    "image/webp",
    "application/pdf",
  ];
  const allowedExtensions = [".png", ".webp", ".pdf"];

  return validateFile(file, {
    allowedTypes,
    allowedExtensions,
    errorMessage:
      "Unsupported file format. Please upload a PNG, WEBP, or PDF file.",
  });
}

/**
 * Validates a contract file (PDF, Images, DOCX/DOC)
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export function validateContractFile(file) {
  const allowedTypes = [
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ];
  const allowedExtensions = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".docx", ".doc"];

  return validateFile(file, {
    allowedTypes,
    allowedExtensions,
    errorMessage:
      "Contract must be a PDF, Image (PNG, JPG, JPEG, WEBP), or DOCX file.",
  });
}
